import type { ExportSettings } from '@/types';

// Characters invalid on Windows: \ / : * ? " < > |  — plus control characters.
// We also trim trailing dots/spaces (invalid as the final character on Windows)
// and collapse repeated separators for readability.
const INVALID_CHARS_RE = /[\\/:*?"<>|\u0000-\u001f]/g;
const RESERVED_WINDOWS_NAMES = new Set([
  'CON', 'PRN', 'AUX', 'NUL',
  'COM1', 'COM2', 'COM3', 'COM4', 'COM5', 'COM6', 'COM7', 'COM8', 'COM9',
  'LPT1', 'LPT2', 'LPT3', 'LPT4', 'LPT5', 'LPT6', 'LPT7', 'LPT8', 'LPT9',
]);

/**
 * Sanitises a string so it is a safe file name on both Windows and macOS.
 * - Strips characters invalid on Windows (\ / : * ? " < > | and control chars).
 * - Trims trailing dots/spaces (Windows disallows a trailing dot or space).
 * - Avoids Windows reserved device names (CON, PRN, NUL, COM1...).
 * - Collapses whitespace to single hyphens and limits length.
 */
export function sanitizeFileName(input: string, fallback = 'reel'): string {
  let name = input
    .normalize('NFKD')
    .replace(INVALID_CHARS_RE, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/[.\s]+$/g, '');

  if (!name) name = fallback;
  if (RESERVED_WINDOWS_NAMES.has(name.toUpperCase())) name = `${name}-file`;

  const MAX_LENGTH = 120;
  if (name.length > MAX_LENGTH) name = name.slice(0, MAX_LENGTH);

  return name;
}

export interface FileNameContext {
  title: string;
  exportSettings: Pick<
    ExportSettings,
    'fileNameMode' | 'customFileName' | 'fileNamePrefix' | 'fileNameSuffix' | 'useSequentialNumbering'
  >;
  /** 1-based index within the bulk batch; only used when useSequentialNumbering is true. */
  sequenceIndex?: number;
  extension?: string;
}

/** Builds the final, sanitised, extension-qualified file name for one export. */
export function buildFileName({ title, exportSettings, sequenceIndex, extension = 'mp4' }: FileNameContext): string {
  const base =
    exportSettings.fileNameMode === 'custom' && exportSettings.customFileName.trim()
      ? exportSettings.customFileName
      : title || 'reel';

  const parts = [exportSettings.fileNamePrefix?.trim(), sanitizeFileName(base)].filter(Boolean);
  let name = parts.join('-');

  if (exportSettings.useSequentialNumbering && sequenceIndex !== undefined) {
    name += `-${String(sequenceIndex).padStart(3, '0')}`;
  }
  if (exportSettings.fileNameSuffix?.trim()) {
    name += `-${sanitizeFileName(exportSettings.fileNameSuffix)}`;
  }

  return `${sanitizeFileName(name)}.${extension}`;
}
